import React, {useCallback, useContext, useEffect, useState} from 'react';

import {FormInput} from 'src/components/input';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';


import {
    ADD_PROJECT_CODE_CONTROL,
    EXISTING_PROJECT_CODES_ERROR,
    EXISTING_PROJECT_CODES_MESSAGE,
} from './constants';

import type {UserProjectCode} from 'src/openapi/admin/models/UserProjectCode';

type Props = {
    codes: UserProjectCode[],
    onCreate: (code: string) => void,
    onClose: () => void,
};

export const AddProjectCodeModal = ({codes, onCreate, onClose}: Props) => {
    const [code, setCode] = useState<string>('');
    const {addError, removeError, validationErrors} = useContext(ValidationContext);

    useEffect(() => {
        return () => {
            removeError(ADD_PROJECT_CODE_CONTROL);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const updateCode = useCallback((newCode: string) => {
        setCode(newCode);
        if (codes.map(({description}) => description).includes(newCode)) {
            addError({
                controlId: ADD_PROJECT_CODE_CONTROL,
                code: EXISTING_PROJECT_CODES_ERROR,
                message: EXISTING_PROJECT_CODES_MESSAGE,
            });
        } else {
            removeError(ADD_PROJECT_CODE_CONTROL);
        }
    }, [codes, addError, removeError]);

    const addProjectCode = useCallback(() => {
        onCreate(code);
        onClose();
    }, [code, onCreate, onClose]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Add Project Code
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <CustomValidatedControl name={ADD_PROJECT_CODE_CONTROL}>
                    {error => (
                        <FormInput
                            label="Project Code"
                            maxLength={128}
                            placeholder="Enter project code"
                            width={300}
                            value=""
                            onChange={updateCode}
                            valid={error ? false : undefined}
                            errorDescription={error}
                        />
                    )}
                </CustomValidatedControl>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        disabled={!code || !!validationErrors.length}
                        onClick={addProjectCode}>
                        Add
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
